/**
 * 공식 문서 조립 (DOCUMENT_OUTPUTS §2~§5). 누적 구조화 자료를 장신상담센터 양식으로 재조합한다.
 * 각 항목은 auto(자동 채움) / needs-review(상담자 확인 필요) / missing(자료 없음)으로 표시한다.
 * 자료가 없는 항목을 추측으로 채우지 않는다. 상담자 성찰·신학적 성찰은 대신 쓰지 않는다.
 * 실명은 저장하지 않고 자리표시자로 두며, 화면·인쇄 시점에만 결합한다.
 */
import type {
  CaseFormulationRevision,
  ClinicalEvidence,
  Client,
  CounselingGoal,
  CounselingJournal,
  CounselorReflection,
  DiagnosticConsideration,
  GoalProgress,
  IntakeSummary,
  PsychologicalAssessment,
  Session,
  SessionAnalysis,
  SupervisionNote,
} from '../schema/v1';
import type { FORMULATION_ITEM_FIELDS } from './formulation';
import { JOURNAL_FIELD_LABELS } from './journal';
import { ageText } from './age';
import { localDateOf } from './dates';
import { numberDialogue, numberVerbatim, numberVerbatimTidy } from './docModel';
import { maskInstitutions } from './mask';
import { itemsTable } from '../assessment/sct';

export const NAME_PLACEHOLDER = '{{이름}}';
export type FieldStatus = 'auto' | 'needs-review' | 'missing' | 'user';
export type Composed = { content: string; fieldSources: Record<string, FieldStatus> };

const MISSING = '[자료 부족 — 추가 확인 필요]';
const REVIEW = '[상담자 직접 작성 필요]';

class Doc {
  private parts: string[] = [];
  readonly sources: Record<string, FieldStatus> = {};
  title(t: string) {
    this.parts.push(`# ${t}`, '');
  }
  section(t: string) {
    this.parts.push(`## ${t}`, '');
  }
  /** opts.header: 세부 칸(####)만 거느리는 머리 칸 — 본문·표시 없음 */
  field(
    key: string,
    label: string,
    value: string | undefined | null,
    opts: { review?: boolean; header?: boolean } = {},
  ) {
    this.put('###', key, label, value, opts);
  }
  sub(key: string, label: string, value: string | undefined | null, opts: { review?: boolean } = {}) {
    this.put('####', key, label, value, opts);
  }
  private put(
    h: string,
    key: string,
    label: string,
    value: string | undefined | null,
    opts: { review?: boolean; header?: boolean },
  ) {
    if (opts.header) {
      this.parts.push(`${h} ${label}`, '');
      return;
    }
    const has = !!value && value.trim().length > 0;
    this.sources[key] = has ? (opts.review ? 'needs-review' : 'auto') : opts.review ? 'needs-review' : 'missing';
    this.parts.push(`${h} ${label}`, has ? value!.trim() : opts.review ? REVIEW : MISSING, '');
  }
  line(text: string) {
    this.parts.push(text);
  }
  build(): Composed {
    return { content: this.parts.join('\n').trimEnd() + '\n', fieldSources: this.sources };
  }
}

const bullets = (xs: (string | undefined)[]) => {
  const v = xs.filter((x): x is string => !!x && !!x.trim());
  return v.length ? v.map((x) => `- ${x}`).join('\n') : undefined;
};

const age = (c: Client, on: string, birthDate?: string) =>
  ageText({ ...(c.birthYear ? { birthYear: c.birthYear } : {}), ...(birthDate ? { birthDate } : {}), on }) ??
  c.ageRange;

const GOAL_STATUS: Record<string, string> = {
  not_started: '시작 전',
  exploring: '탐색 중',
  progressing: '진전 있음',
  maintaining: '유지',
  achieved: '달성',
  paused: '보류',
  terminated: '종결',
};
export const goalStatusLabel = (s: string) => GOAL_STATUS[s] ?? s;

/** 확인된 내담자 진술 인용: 같은 문장은 한 번만, 나온 회기를 모아서 "(1·2·3회기)" */
function quoteLines(quotes: ClinicalEvidence[]): string | undefined {
  const by = new Map<string, Set<number>>();
  for (const q of quotes) {
    const key = q.text.replace(/\s+/g, ' ').trim();
    const ns = by.get(key) ?? new Set<number>();
    if (q.sourceSession) ns.add(q.sourceSession);
    by.set(key, ns);
  }
  if (!by.size) return undefined;
  return [...by]
    .map(([text, ns]) => {
      const list = [...ns].sort((a, b) => a - b);
      return `“${text}”${list.length ? ` (${list.join('·')}회기)` : ''}`;
    })
    .join('\n');
}

// ---------- 상담기록지 ----------

export function composeSessionRecord(input: {
  client: Client;
  session: Session;
  journal: CounselingJournal;
  birthDate?: string;
  counselorName?: string;
}): Composed {
  const { client, session, journal } = input;
  const d = new Doc();
  d.title(`상담기록지 — ${client.clientCode} ${session.sessionNumber}회기`);
  d.line(
    `상담일: ${session.sessionDate} · 시간: ${session.durationMinutes ?? '-'}분 · 회기: ${session.sessionNumber} · 나이: ${age(client, session.sessionDate, input.birthDate) ?? '-'} · 성별: ${client.gender ?? '-'} · 이름: ${NAME_PLACEHOLDER} · 상담자: ${input.counselorName || '____'}`,
  );
  d.line('');
  d.field('counselingReasonAndGoal', JOURNAL_FIELD_LABELS.counselingReasonAndGoal, journal.counselingReasonAndGoal);
  d.field('assessment', JOURNAL_FIELD_LABELS.assessment, journal.assessment);
  d.field('pastCounselingExperience', JOURNAL_FIELD_LABELS.pastCounselingExperience, journal.pastCounselingExperience);
  d.field('importantIssue', JOURNAL_FIELD_LABELS.importantIssue, journal.importantIssue);
  d.field(
    'previousSessionIssueOrTask',
    JOURNAL_FIELD_LABELS.previousSessionIssueOrTask,
    [
      journal.previousSessionIssueOrTask,
      journal.previousTaskCompletion && `수행 여부: ${journal.previousTaskCompletion}`,
    ]
      .filter(Boolean)
      .join('\n'),
  );
  d.field('counselingContent', JOURNAL_FIELD_LABELS.counselingContent, journal.counselingContent);
  d.field('nextSessionPlan', JOURNAL_FIELD_LABELS.nextSessionPlan, journal.nextSessionPlan);
  d.field('remarks', JOURNAL_FIELD_LABELS.remarks, journal.remarks);
  return d.build();
}

// ---------- 상담사례 보고서 ----------

export type CaseReportInput = {
  client: Client;
  intake?: IntakeSummary;
  /** asOf 시점까지 승인된 회기 */
  sessions: Session[];
  journals: CounselingJournal[];
  analyses: SessionAnalysis[];
  evidence: ClinicalEvidence[];
  formulation?: CaseFormulationRevision;
  diagnosis?: DiagnosticConsideration;
  goals: CounselingGoal[];
  goalProgress: GoalProgress[];
  assessments: PsychologicalAssessment[];
  supervision: SupervisionNote[];
  reflections: CounselorReflection[];
  asOf: string;
  /** 민감정보의 생년월일(있으면 정확한 만 나이). 문서에는 나이만 들어간다. */
  birthDate?: string;
  /** 상담자가 고른 발췌·축어록 (실제 발언, 화자 표시) */
  verbatims?: VerbatimBlock[];
  /** 설정의 상담자 정보 (Amendment A25) */
  counselor?: { name?: string; defaultLocation?: string };
  /** 사례개념화에서 상담자가 강화·약화로 표시한 가설 (가장 최근 표시) */
  hypothesisMarks?: Record<string, 'strengthened' | 'weakened'>;
};

export type VerbatimBlock = {
  sessionNumber: number;
  label: string;
  startMs: number;
  endMs: number;
  reason?: string;
  supervisionQuestions?: string[];
  lines: { role: string; text: string; startMs: number; uncertain: boolean; backchannel?: boolean }[];
};

const mmss = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const p = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}` : `${p(Math.floor(s / 60))}:${p(s % 60)}`;
};
const ROLE_MARK: Record<string, string> = { counselor: '상', client: '내', unknown: '?', mixed: '?' };

export function renderVerbatims(blocks: VerbatimBlock[]): { text?: string; uncertain: boolean } {
  if (!blocks.length) return { uncertain: false };
  let uncertain = false;
  const parts = blocks.map((b) => {
    if (b.lines.some((l) => l.uncertain)) uncertain = true;
    return [
      `[S${b.sessionNumber} ${mmss(b.startMs)}–${mmss(b.endMs)} · ${b.label}]`,
      ...(b.reason ? [`선택 이유: ${b.reason}`] : []),
      ...b.lines.map((l) => `${ROLE_MARK[l.role] ?? '?'}${l.uncertain ? '(확인 필요)' : ''}: ${l.text}`),
      ...(b.supervisionQuestions?.length ? [`수퍼비전 질문: ${b.supervisionQuestions.join(' / ')}`] : []),
    ].join('\n');
  });
  return { text: parts.join('\n\n'), uncertain };
}

/**
 * 상담사례 보고서 — 장신상담센터 기본 양식(상담사례보고서 기본 양식_최종) 순서·번호 그대로.
 * 앱에 자료가 없는 칸(학교·학점, 장소, 가계도, 애착 등)은 비워 두고 [자료 부족]으로 표시한다.
 * IV(상담자 자신)·VI-2·VII·VIII은 상담자가 직접 쓴다 (기록해 둔 성찰만 옮겨 놓음).
 */
export function composeCaseReport(i: CaseReportInput): Composed {
  const d = new Doc();
  const done = [...i.sessions].sort((a, b) => a.sessionNumber - b.sessionNumber);
  const isOngoing = i.client.status !== 'terminated';
  const f = i.formulation;
  const ev = (t: ClinicalEvidence['evidenceType']) => i.evidence.filter((e) => e.evidenceType === t && !e.deletedAt);
  const dom = i.intake?.domains ?? {};
  const ageNow = age(i.client, localDateOf(i.asOf), i.birthDate);
  const rejected = new Set(f?.rejectedHypotheses ?? []);
  const items = (k: (typeof FORMULATION_ITEM_FIELDS)[number]) =>
    (f?.[k] ?? [])
      .filter((x) => !rejected.has(x.id))
      .map((x) => {
        const mark = i.hypothesisMarks?.[x.id];
        if (x.status !== 'hypothesis') return x.text;
        return mark === 'strengthened'
          ? `(가설·강화됨) ${x.text}`
          : mark === 'weakened'
            ? `(가설·약화됨) ${x.text}`
            : `(가설) ${x.text}`;
      });
  const numbered = (xs: string[]) => (xs.length ? xs.map((x, k) => `${k + 1}) ${x}`).join('\n') : undefined);

  d.title('상담사례 보고서');
  d.line(`일시: ____ · 슈퍼바이저: ____ · 발표자: ${i.counselor?.name || '____'}`);
  d.line(
    `내담자: ${NAME_PLACEHOLDER} (${i.client.clientCode}) · 작성 기준일: ${localDateOf(i.asOf)} · 진행 회기: ${done.length}회${isOngoing ? ' (진행 중)' : ''}`,
  );
  d.line('');

  // I
  d.section('I. 주요 호소 문제');
  const quotes = ev('CLIENT_REPORT').filter((e) => e.verified);
  d.field('presentingProblems', '내담자가 직접 표현한 호소', quoteLines(quotes));
  d.field('presentingSummary', '주호소 정리', bullets(i.intake?.presentingProblems ?? []));

  // II
  d.section('II. 임상 자료');
  d.field(
    'demographics',
    '1. 인적 사항',
    bullets([
      `이름: ${NAME_PLACEHOLDER}`,
      i.client.gender && `성별: ${i.client.gender}`,
      ageNow && `나이: ${ageNow}`,
      i.client.education && `학교(학점): ${maskInstitutions(i.client.education)}`,
      i.client.religion && `종교: ${maskInstitutions(i.client.religion)}`,
      i.client.occupationRole && `직업: ${maskInstitutions(i.client.occupationRole)}`,
      i.client.substanceUse && `알코올 등 물질 관련: ${i.client.substanceUse}`,
      i.client.medicalPsychiatricHistory && `질병: ${i.client.medicalPsychiatricHistory}`,
      i.client.currentMedication?.length ? `복용 약물: ${i.client.currentMedication.join(', ')}` : undefined,
      dom.health && `건강: ${dom.health}`,
    ]),
    { review: true },
  );
  d.field(
    'referral',
    '2. 내담 경위 및 이전 상담 경험과 평가',
    bullets([
      i.client.referralSource && `상담기관·내방경위: ${i.client.referralSource}`,
      i.intake?.referralReason && `상담 신청 사유: ${i.intake.referralReason}`,
      i.client.counselingContext,
      i.client.priorCounselingHistory && `이전 상담 경험: ${i.client.priorCounselingHistory}`,
    ]),
  );
  d.field('observation', '3. 인상 및 행동 관찰', bullets(ev('COUNSELOR_OBSERVATION').map((e) => e.text)));
  const tests = i.assessments.filter((a) => !a.deletedAt);
  d.field('tests', '4. 심리검사 결과', undefined, { header: true });
  d.sub(
    'tests.results',
    '1) 실시한 검사',
    bullets(
      tests.map((a) =>
        `${a.testName}${a.administeredAt ? ` (${a.administeredAt})` : ''}: ${a.scoresSummary ?? ''}`.trim(),
      ),
    ),
  );
  d.sub(
    'tests.interpretation',
    '2) 상담자의 해석',
    bullets(tests.map((a) => a.interpretation && `${a.testName}: ${flatInterpretation(a.interpretation)}`)),
    { review: true },
  );
  d.field('family', '5. 가족 관계', undefined, { header: true });
  d.sub(
    'family.members',
    '1) 가족구성원 (나이·직업·학벌·성격·내담자와의 관계)',
    bullets([i.client.familyStructure, dom.family]),
  );
  d.sub('family.genogram', '2) 가계도 (Genogram)', undefined, { review: true });
  d.field(
    'social',
    '6. 사회적 관계 (직업 및 학교생활)',
    bullets([
      ...(i.client.importantRelationships ?? []),
      dom.relationship,
      dom.social,
      dom.schoolWork && `직업·학교: ${dom.schoolWork}`,
    ]),
  );
  d.field('history', '7. 발달사 및 주요 호소문제와 관련된 주요 사건', undefined, { header: true });
  d.sub('history.attachment', '1) 초기의 애착 대상과의 관계', undefined);
  d.sub(
    'history.events',
    '2) 청소년 및 성인기 발달과 관련된 주요사건, 가족관계, 성취·실패 경험',
    bullets(i.client.majorLifeEvents ?? []),
  );
  d.sub(
    'history.recent',
    '3) 최근 문제의 발생 및 유지와 관련된 발달정보',
    bullets([
      i.intake?.onset && `시작: ${i.intake.onset}`,
      i.intake?.duration && `기간: ${i.intake.duration}`,
      i.intake?.frequency && `빈도: ${i.intake.frequency}`,
      i.intake?.severity && `심각도: ${i.intake.severity}`,
      i.intake?.functionalImpact && `기능 영향: ${i.intake.functionalImpact}`,
    ]),
  );
  d.field(
    'strengths',
    '8. 내담자의 강점과 약점',
    bullets([
      ...(i.intake?.strengths ?? []).map((s) => `강점: ${s}`),
      ...(i.intake?.copingResources ?? []).map((s) => `대처자원: ${s}`),
      ...(i.intake?.supportResources ?? []).map((s) => `지지자원: ${s}`),
      ...(f?.protectiveFactors ?? []).map((s) => `보호요인: ${s}`),
      ...(f?.predisposingFactors ?? []).map((s) => `취약점: ${s}`),
    ]),
  );
  d.field('faith', '9. 기독교인으로서 내담자의 신앙에 대한 이해', undefined, { header: true });
  d.sub('faith.god', '1) 내담자의 하나님 이해', undefined, { review: true });
  d.sub(
    'faith.life',
    '2) 내담자의 신앙생활에 대한 이해',
    bullets([i.client.faithSpiritualContext, dom.faithSpirituality]),
    {
      review: true,
    },
  );
  d.sub('faith.human', '3) 내담자의 인간에 대한 이해', undefined, { review: true });

  // III
  d.section('III. 내담자 이해와 상담 방향성 (사례개념화)');
  d.field(
    'formulation.problems',
    '① 주호소문제 및 문제행동',
    bullets([...(f?.currentProblems ?? []), ...items('behavioralPatterns')]),
  );
  d.field(
    'formulation.causes',
    '② 문제행동 이면의 유발요인 (신앙 요인 포함)',
    bullets([
      ...(f?.predisposingFactors ?? []).map((x) => `취약요인: ${x}`),
      ...items('coreCognitiveThemes').map((x) => `핵심 인지: ${x}`),
      ...items('beliefHypotheses').map((x) => `신념: ${x}`),
      ...items('coreEmotions').map((x) => `핵심 정서: ${x}`),
    ]),
  );
  d.field(
    'formulation.process',
    '③ 문제행동을 촉발·유지시키는 과정',
    bullets([
      ...(f?.precipitatingFactors ?? []).map((x) => `촉발: ${x}`),
      ...(f?.perpetuatingFactors ?? []).map((x) => `유지: ${x}`),
      ...items('relationalPatterns').map((x) => `관계 패턴: ${x}`),
      ...items('therapyInterferingPatterns').map((x) => `상담 방해 패턴: ${x}`),
    ]),
  );
  d.field(
    'formulation.theory',
    '관련 상담 이론에 근거한 통합 가설',
    f?.theoryFormulations?.length
      ? f.theoryFormulations
          .map(
            (t) =>
              `- [${t.orientation}, 적합도 ${t.fitLevel}] ${t.formulation} (지지 근거 ${t.supportingEvidenceIds?.length ?? 0} / 반증 ${t.contradictingEvidenceIds?.length ?? 0})`,
          )
          .join('\n')
      : undefined,
  );

  // IV — 상담자가 직접
  d.section('IV. 상담자 자신에 대한 이해');
  d.field('iv.theology', '1. 사례(내담자)에 관한 상담자의 신학적 태도', undefined, { header: true });
  d.sub('selfUnderstanding', '1) 상담자 자신의 하나님이해', undefined, { review: true });
  d.sub('iv.human', '2) 상담자 자신의 인간이해', undefined, { review: true });
  d.sub('iv.faith', '3) 상담자로서 이해하는 신앙인으로서 자세와 삶', undefined, { review: true });
  d.sub('iv.view', '4) 내담자에 대한 상담자의 신학적 관점 분석', undefined, { review: true });
  d.field('iv.psych', '2. 사례(내담자)에 관한 상담자의 상담심리학적 관점', undefined, { header: true });
  d.sub(
    'iv.theory',
    '1) 상담자로서 택한 상담이론',
    bullets([...new Set(f?.theoryFormulations?.map((t) => t.orientation) ?? [])]),
    {
      review: true,
    },
  );
  d.sub('iv.method', '2) 사례(내담자)를 위한 상담방법론', undefined, { review: true });
  d.field('iv.dialogue', '3. 상담자의 학문(신학과 상담심리학)간 대화의 특징에 대한 서술', undefined, { review: true });

  // V
  d.section('V. 상담 목표 / 전략 및 개입 절차');
  const goals = i.goals.filter((g) => !g.deletedAt);
  d.field('goals', '1. 상담 목표', undefined, { header: true });
  d.sub(
    'goals.agreed',
    '1) 합의한 상담 목표 (내담자의 표현으로)',
    numbered(goals.filter((g) => g.agreedWithClient).map((g) => g.title)),
  );
  d.sub(
    'goals.clinical',
    '2) 임상적 상담 목표 (상담자로서 제공할 목표 및 전략)',
    numbered(goals.filter((g) => !g.agreedWithClient).map((g) => g.title)),
  );
  const interventions = [
    ...new Set(
      i.analyses.flatMap((a) =>
        (a.interventions ?? []).map((x) =>
          x.theoreticalOrientation ? `${x.interventionName} (${x.theoreticalOrientation})` : x.interventionName,
        ),
      ),
    ),
  ];
  d.field('strategy', '2. 상담 전략', numbered(interventions));
  const journalBySession = new Map(i.journals.map((j) => [j.sessionId, j]));
  const analysisBySession = new Map(i.analyses.map((a) => [a.sessionId, a]));
  const theme = (s: Session) =>
    journalBySession.get(s.id)?.importantIssue ?? s.primaryThemes?.join(', ') ?? s.sessionFocus ?? '';
  const cellText = (t: string) => t.replace(/\s*\n\s*/g, ' ').replace(/\|/g, '/');
  d.field(
    'sessionTable',
    '3. 상담진행 과정 및 회기 주제',
    [
      `| 최대 상담 가능 회기 수 | ${i.client.maxSessions ? `${i.client.maxSessions}회` : '____'} |`,
      '| 회기 | 날짜(소요시간) | 회기 주제 |',
      ...done.map(
        (s) =>
          `| ${s.sessionNumber} | ${s.sessionDate} (${s.durationMinutes ?? '-'}분) | ${cellText(theme(s)) || '-'} |`,
      ),
    ].join('\n'),
  );
  if (done.length) d.field('sessionSummaries', '4. 상담 회기 요약', undefined, { header: true });
  else d.field('sessionSummaries', '4. 상담 회기 요약', undefined);
  for (const s of done) {
    const j = journalBySession.get(s.id);
    const a = analysisBySession.get(s.id);
    const iv = (a?.interventions ?? []).map((x) => x.interventionName).join(', ');
    d.sub(
      `session.${s.sessionNumber}`,
      `${s.sessionNumber}회기`,
      [
        `날짜: ${s.sessionDate} · 시간: ${s.durationMinutes ?? '-'}분 · 장소: ${s.location || i.counselor?.defaultLocation || '____'}`,
        a?.sessionSummary ?? j?.counselingContent ?? j?.importantIssue,
        iv && `개입 방법: ${iv}`,
      ]
        .filter(Boolean)
        .join('\n'),
    );
  }
  const vbs = i.verbatims ?? [];
  d.field('verbatim.head', '5. 축어록 (10~15분 축어록으로 정리)', undefined, { header: true });
  d.sub(
    'verbatim.reason',
    '1) 이 부분을 축어록으로 만든 이유',
    bullets(vbs.map((b) => b.reason && (vbs.length > 1 ? `${b.sessionNumber}회기: ${b.reason}` : b.reason))),
    { review: true },
  );
  d.sub(
    'verbatim.time',
    '2) 시작과 끝나는 시간',
    bullets(vbs.map((b) => `${b.sessionNumber}회기 ${mmss(b.startMs)} ~ ${mmss(b.endMs)}`)),
  );
  const uncertain = vbs.some((b) => b.lines.some((l) => l.uncertain));
  d.sub(
    'verbatim',
    '축어록 (상: 상담자, 내: 내담자)',
    vbs.length
      ? vbs
          .map((b) =>
            [
              ...(vbs.length > 1 ? [`[${b.sessionNumber}회기 ${mmss(b.startMs)}–${mmss(b.endMs)}]`] : []),
              ...numberVerbatimTidy(b.lines),
            ].join('\n'),
          )
          .join('\n\n')
      : undefined,
    { review: uncertain },
  );

  // VI
  d.section(isOngoing ? 'VI. 상담 평가 (현재까지)' : 'VI. 상담 평가');
  d.field('evaluation', '1. 호소 문제 달성 평가', undefined, { header: true });
  const num = new Map(done.map((s) => [s.id, s.sessionNumber]));
  d.sub(
    'evaluation.client',
    '1) 내담자의 평가',
    bullets(
      i.goalProgress
        .filter((p) => p.clientAssessment && num.has(p.sessionId))
        .map(
          (p) =>
            `${num.get(p.sessionId)}회기 ${goals.find((g) => g.id === p.goalId)?.title ?? ''}: ${p.clientAssessment}`,
        ),
    ),
    { review: true },
  );
  d.sub('evaluation.counselor', '2) 상담자의 평가', goalEvaluation(goals, i.goalProgress, done));
  const selfNotes = i.reflections.filter((r) => r.kind === 'self-understanding' && !r.deletedAt);
  d.field('evaluation.self', '2. 상담자 자신에 대한 평가', bullets(selfNotes.map((r) => r.text)), { review: true });

  // VII
  d.section('VII. 수퍼비전을 통해 도움 받고 싶은 점');
  d.field(
    'supervision',
    '수퍼비전 질문',
    bullets(i.supervision.filter((n) => !n.deletedAt).map((n) => n.supervisionQuestion ?? n.counselorDifficulty)),
    { review: true },
  );

  // VIII
  d.section('VIII. 신학적 성찰');
  const theo = i.reflections.filter((r) => r.kind === 'theological-reflection' && !r.deletedAt);
  d.field('theology', '1. 목회상담으로서 본 상담에 대한 (목회신학적) 평가', bullets(theo.map((r) => r.text)), {
    review: true,
  });
  d.field('theology.pastoral', '2. 본 상담을 목회상담이라고 부를 수 있는 요인과 특징에 대한 서술', undefined, {
    review: true,
  });

  // 별지: 문항별 응답이 있는 검사 (문장완성검사 등)
  tests
    .filter((a) => a.items?.length)
    .forEach((a, k) => {
      if (k === 0) d.section('별지');
      d.field(
        `appendix.${a.id}`,
        `별지 ${k + 1}. ${a.testName}${a.administeredAt ? ` (${a.administeredAt})` : ''} 문항별 응답`,
        itemsTable(a.items!),
      );
    });

  return d.build();
}

function goalEvaluation(goals: CounselingGoal[], progress: GoalProgress[], sessions: Session[]): string | undefined {
  if (!goals.length) return undefined;
  const num = new Map(sessions.map((s) => [s.id, s.sessionNumber]));
  return goals
    .map((g) => {
      const steps = progress
        .filter((p) => p.goalId === g.id && p.approvedStatus && num.has(p.sessionId))
        .sort((a, b) => num.get(a.sessionId)! - num.get(b.sessionId)!)
        .map((p) => `S${num.get(p.sessionId)} ${goalStatusLabel(p.approvedStatus!)}`);
      return `- ${g.title}: 현재 ${goalStatusLabel(g.status)}${steps.length ? ` (${steps.join(' → ')})` : ''}`;
    })
    .join('\n');
}

// ---------- 종결 보고서 ----------

const TERMINATION_REASON: Record<string, string> = {
  plan_completed: '상담 계획 완료',
  client_request: '내담자의 종료 희망',
  referral: '타 기관 연계',
  other: '기타',
};

/**
 * 상담 종결 보고서 (DOCUMENT_OUTPUTS §4). 기관 원본 양식이 없어 사례보고서 기본 양식과 같은 모양으로 구성:
 * 가운데 제목 · 머리 칸 · 로마숫자 큰 항목 · 1. / 1) 세부 칸 · 표.
 */
export function composeTerminationReport(i: CaseReportInput): Composed {
  if (i.client.status !== 'terminated') throw new Error('종결 처리된 내담자만 종결보고서를 만들 수 있습니다');
  const d = new Doc();
  const done = [...i.sessions].sort((a, b) => a.sessionNumber - b.sessionNumber);
  const goals = i.goals.filter((g) => !g.deletedAt);
  const numbered = (xs: string[]) => (xs.length ? xs.map((x, k) => `${k + 1}) ${x}`).join('\n') : undefined);
  const cellText = (t: string) => t.replace(/\s*\n\s*/g, ' ').replace(/\|/g, '/');
  const journalBySession = new Map(i.journals.map((j) => [j.sessionId, j]));
  const analysisBySession = new Map(i.analyses.map((a) => [a.sessionId, a]));
  const num = new Map(done.map((s) => [s.id, s.sessionNumber]));

  d.title('상담 종결 보고서');
  d.line(
    `작성일: ${localDateOf(i.asOf)} · 상담자: ${i.counselor?.name || '____'} · 내담자: ${NAME_PLACEHOLDER} (${i.client.clientCode})`,
  );
  d.line('');

  // I
  d.section('I. 상담 개요');
  d.field(
    'period',
    '1. 상담 진행 기간',
    done.length ? `${done[0]!.sessionDate} ~ ${i.client.terminationDate ?? done.at(-1)!.sessionDate}` : undefined,
  );
  d.field('count', '2. 총 상담 횟수', done.length ? `${done.length}회` : undefined);
  const reason = i.client.terminationReason;
  d.field(
    'reason',
    '3. 상담 종결 사유',
    reason
      ? [
          (Object.keys(TERMINATION_REASON) as (keyof typeof TERMINATION_REASON)[])
            .map((k) => `${k === reason ? '■' : '□'} ${TERMINATION_REASON[k]}`)
            .join('   '),
          i.client.terminationNote && `(${i.client.terminationNote})`,
        ]
          .filter(Boolean)
          .join('\n')
      : undefined,
  );

  // II
  d.section('II. 주 호소 문제');
  const quotes = i.evidence.filter((e) => e.evidenceType === 'CLIENT_REPORT' && e.verified && !e.deletedAt);
  d.field('presenting', '주 호소 문제', bullets(i.intake?.presentingProblems ?? []));
  if (quotes.length) d.field('presentingWords', '내담자의 표현', quoteLines(quotes));

  // III
  d.section('III. 상담 목표');
  d.sub('goals', '1) 합의한 상담 목표', numbered(goals.filter((g) => g.agreedWithClient).map((g) => g.title)));
  d.sub(
    'goals.clinical',
    '2) 임상적 상담 목표',
    numbered(goals.filter((g) => !g.agreedWithClient).map((g) => g.title)),
  );

  // IV
  d.section('IV. 상담 전략');
  d.field(
    'strategy',
    '상담 전략 및 주요 개입',
    numbered([
      ...new Set(
        i.analyses.flatMap((a) =>
          (a.interventions ?? []).map((x) =>
            x.theoreticalOrientation ? `${x.interventionName} (${x.theoreticalOrientation})` : x.interventionName,
          ),
        ),
      ),
    ]),
  );

  // V
  d.section('V. 상담 과정 요약');
  const approved = (gId: string) =>
    i.goalProgress
      .filter((p) => p.goalId === gId && p.approvedStatus && num.has(p.sessionId))
      .sort((a, b) => num.get(a.sessionId)! - num.get(b.sessionId)!);
  d.field(
    'achievement',
    '1. 상담 목표 달성 정도',
    goals.length
      ? [
          '| 목표 | 진행 경과 | 종결 시 상태 |',
          ...goals.map((g) => {
            const steps = approved(g.id).map(
              (p) => `${num.get(p.sessionId)}회기 ${goalStatusLabel(p.approvedStatus!)}`,
            );
            return `| ${cellText(g.title)} | ${steps.join(' → ') || '-'} | ${goalStatusLabel(g.status)} |`;
          }),
        ].join('\n')
      : undefined,
  );
  d.field(
    'course',
    '2. 상담 경과',
    done.length
      ? [
          '| 회기 | 날짜(소요시간) | 회기 주제 |',
          ...done.map((s) => {
            const t =
              analysisBySession.get(s.id)?.sessionSummary ??
              journalBySession.get(s.id)?.importantIssue ??
              s.primaryThemes?.join(', ') ??
              '-';
            return `| ${s.sessionNumber} | ${s.sessionDate} (${s.durationMinutes ?? '-'}분) | ${cellText(t)} |`;
          }),
        ].join('\n')
      : undefined,
  );
  d.field('outcomeHead', '3. 상담 결과', undefined, { header: true });
  d.sub(
    'outcome',
    '1) 변화된 점',
    bullets(
      goals
        .filter((g) => g.status === 'achieved' || g.status === 'maintaining')
        .map((g) => `${g.title} (${goalStatusLabel(g.status)})`),
    ),
    { review: true },
  );
  d.sub(
    'outcome.remaining',
    '2) 남아 있는 어려움',
    bullets(
      goals
        .filter((g) => g.status !== 'achieved' && g.status !== 'maintaining')
        .map((g) => `${g.title} (${goalStatusLabel(g.status)})`),
    ),
    { review: true },
  );
  d.sub(
    'outcome.after',
    '3) 보호요인 및 종결 후 제언',
    bullets((i.formulation?.protectiveFactors ?? []).map((p) => `보호요인: ${p}`)),
    { review: true },
  );

  // VI
  d.section('VI. 상담자 소감');
  d.field('reflection', '상담자 소감 및 배운 점', undefined, { review: true });
  return d.build();
}

// ---------- 축어록 ----------

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];
/** 2026-09-15 → 2026.9.15.(화) */
function dotDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${y}.${m}.${d}.(${WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]})`;
}
/** 머리 칸 값: 줄바꿈·구분 기호가 칸을 깨지 않게 */
const cell = (t: string | undefined, fallback = '____') =>
  t?.trim()
    ? t
        .replace(/\s*\n\s*/g, ', ')
        .replace(/\s·\s/g, ', ')
        .replace(/:/g, '：')
    : fallback;

/**
 * 대화록(Verbatim) — 수업용 목회상담 대화록 양식 (Amendment A26): 머리 표 10칸 + I 사실 파악과 준비 · II 관찰 · III 면담 · IV 분석 · V 신학적 의미 · VI 앞으로의 계획.
 * 내담자명은 가명(별칭)만 쓴다. I·IV·V는 상담자가 직접 쓴다.
 */
/** 대화록 부록 (수업 발표 준비) */
export const DIALOGUE_APPENDIX = '부록. 수업 발표 준비';
export const DIALOGUE_QA_EXPECTED = '발표 예상 질문과 답변 요지';
export const DIALOGUE_QA_MINE = '수업에서 던질 질문';

export function composeDialogueRecord(i: {
  client: Client;
  session: Session;
  block: VerbatimBlock;
  /** 이 내담자의 몇 번째 대화록인지 */
  serial: number;
  counselorName?: string;
  journal?: CounselingJournal;
  observations?: string[];
  memoText?: string;
  plannedObjectives?: string[];
  birthDate?: string;
}): Composed {
  const { client, session, block } = i;
  const d = new Doc();
  const minutes = Math.max(1, Math.round((block.endMs - block.startMs) / 60000));
  const topic = i.journal?.importantIssue ?? session.primaryThemes?.join(', ') ?? session.sessionFocus;
  d.title('대화록(Verbatim)');
  d.line(
    [
      `면담 일(날짜): ${dotDate(session.sessionDate)}`,
      `대화록 일련번호: ${i.serial}`,
      `목회상담자 이름: ${cell(i.counselorName)}`,
      `면담 시간: ${session.durationMinutes ? `약 ${session.durationMinutes}분` : '____'}`,
      `내담자명(가명): ${cell(client.alias, client.clientCode)}`,
      `만남 회수: ${session.sessionNumber}`,
      `나이 / 성별: ${cell(age(client, session.sessionDate, i.birthDate), '-')} / ${cell(client.gender, '-')}`,
      `대화의 주제: ${cell(topic)}`,
      `직업 / 교육: ${cell(client.occupationRole, '-')} / ${cell(client.education, '-')}`,
      `종교(신앙 경력): ${cell(client.religion)}`,
    ].join(' · '),
  );
  d.line('');

  // 목회상담 대화록 작성표: 여섯 구성 요소 (사실 파악과 준비 → 관찰 → 면담 → 분석 → 신학적 의미 → 앞으로의 계획)
  d.section('I. 사실 파악과 준비');
  d.field(
    'preparation',
    '사실 파악과 준비 (만남의 동기 · 사전 정보 · 면담 목표 · 몇 번째 만남 · 나의 감정과 기대)',
    [
      `${session.sessionNumber}번째 만남`,
      i.plannedObjectives?.length ? `이번 만남의 목표 (지난 회기 계획):\n${bullets(i.plannedObjectives)}` : undefined,
    ]
      .filter(Boolean)
      .join('\n'),
    { review: true },
  );

  d.section('II. 관찰');
  d.field(
    'observation',
    '관찰 (만남 장소의 분위기 · 내담자의 표정·옷차림·태도 · 예상과 달랐던 점 · 나에게 주는 인상)',
    [i.memoText?.trim(), bullets(i.observations ?? [])].filter(Boolean).join('\n') || undefined,
    { review: true },
  );

  d.section('III. 면담 (상: 상담자, 내: 내담자)');
  d.line(
    `(녹음 ${mmss(block.startMs)} ~ ${mmss(block.endMs)} 구간, 약 ${minutes}분${block.reason ? ` · 선택 이유: ${block.reason}` : ''})\n`,
  );
  const uncertain = block.lines.some((l) => l.uncertain);
  d.field('verbatim', '대화 내용', block.lines.length ? numberDialogue(block.lines).join('\n') : undefined, {
    review: uncertain,
  });

  d.section('IV. 분석');
  d.field(
    'analysis',
    '분석 (부각된 주제 · 내담자에 대한 의문 · 나의 반응이 대화에 준 영향 · 잘한 것과 아쉬운 것 · 목표와 실제 비교 · 배운 것)',
    undefined,
    { review: true },
  );

  d.section('V. 신학적 의미');
  d.field(
    'theology',
    '신학적 의미 (궁극적 의미와 가치 · 제기되는 신학적 질문과 응답 · 도전받거나 재확인된 나의 신학적 관점)',
    undefined,
    { review: true },
  );

  d.section('VI. 앞으로의 계획');
  d.field(
    'plan',
    '앞으로의 계획 (내담자에게 도움이 될 것 · 도울 자원 · 다음 만남 · 그때의 목표)',
    i.journal?.nextSessionPlan,
    { review: true },
  );

  // 작성표 여섯 항목 뒤에 따로: 수업 발표 준비 (본문 항목과 섞지 않음)
  d.section(DIALOGUE_APPENDIX);
  d.field('qa.expected', DIALOGUE_QA_EXPECTED, undefined, { review: true });
  d.field('qa.mine', DIALOGUE_QA_MINE, undefined, { review: true });
  return d.build();
}

export function composeVerbatimDocument(i: { client: Client; session: Session; block: VerbatimBlock }): Composed {
  const { client, session, block } = i;
  const d = new Doc();
  const minutes = Math.max(1, Math.round((block.endMs - block.startMs) / 60000));
  d.title(`축어록 — ${client.clientCode} ${session.sessionNumber}회기`);
  d.line(
    `상담일: ${session.sessionDate} · 회기: ${session.sessionNumber} · 구간: ${mmss(block.startMs)}–${mmss(block.endMs)} (약 ${minutes}분) · 내담자: ${NAME_PLACEHOLDER}`,
  );
  d.line('');
  d.field('reason', '이 구간을 선택한 이유', block.reason, { review: !block.reason });
  const uncertain = block.lines.some((l) => l.uncertain);
  const body = block.lines.length ? numberVerbatim(block.lines).join('\n') : undefined;
  d.field('verbatim', '축어록 (상: 상담자, 내: 내담자)', body, { review: uncertain });
  d.field('supervision', '수퍼비전 질문', bullets(block.supervisionQuestions ?? []), { review: true });
  d.field('process', '상담자 소감 · 과정 분석', undefined, { review: true });
  return d.build();
}

/** 화면·인쇄 시점에만 실명을 결합한다 */
/** 앱이 넣는 안내 줄 ([자료 부족 …], [상담자 직접 작성 필요], [AI 초안 …]) */
const MARK_LINE = /^\s*\[(자료 부족|상담자 직접 작성 필요|AI 초안)[^\]]*\]\s*$/;

/**
 * 내보내기·복사·인쇄용 본문: 앱·AI 흔적을 남기지 않는다.
 * 안내 줄은 지우고(칸은 비워 둠), "(확인 필요)"·"(추정)" 같은 꼬리표, 마크다운 강조, 손글씨 판독 표시([?])를 뗀다.
 * 편집 화면에서는 그대로 보여 상담자가 확인할 수 있게 한다.
 */
export function cleanForOutput(text: string): string {
  return text
    .replace(/\r/g, '')
    .split('\n')
    .filter((l) => !MARK_LINE.test(l) && !/^(BEGIN|END)_COUNSEL_AI_JSON$/.test(l.trim()) && !l.trim().startsWith('```'))
    .map((l) =>
      l
        .replace(/\s*\((손글씨\s*)?확인 필요\)/g, '')
        .replace(/\s*\((추정|AI 초안|AI 제안)\)/g, '')
        .replace(/\[\?([^\]]*)\]/g, '$1')
        .replace(/\*\*(.+?)\*\*/g, '$1'),
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
}

/** 두 글자 성 (남궁·황보·제갈 등) */
const TWO_CHAR_SURNAMES = ['남궁', '황보', '제갈', '선우', '독고', '사공', '서문', '동방', '어금', '망절'];

/** 보고서 이름 표기: 성 + ○○ (예: 홍○○). 이름을 모르면 ○○○ */
export function maskedName(realName: string | undefined): string {
  const n = realName?.trim().replace(/\s+/g, '');
  if (!n) return '○○○';
  const two = TWO_CHAR_SURNAMES.find((s) => n.startsWith(s) && n.length > 2);
  return `${two ?? n[0]}○○`;
}

/** 이름 자리표시자: 실명 표시를 켜면 실명, 아니면 성○○ */
export function renderWithIdentity(content: string, realName: string | undefined, showName: boolean): string {
  return content.split(NAME_PLACEHOLDER).join(showName && realName ? realName : maskedName(realName));
}

/** 해석(### 제목이 있는 글)을 문서 칸 안의 한 단락으로: 제목은 [제목]으로 */
export function flatInterpretation(text: string): string {
  return text
    .replace(/\r/g, '')
    .replace(/^#{1,6}\s*(.+)$/gm, '[$1]')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' ');
}
